/*
 * Page for setting up and maintaining integration settings for Recurly
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Paper } from '@mui/material';
import { Typography, TextField  } from '@mui/material';
import IntegrationSetupMutation from './IntegrationSetupMutation';

import BackButton from '../ui/BackButton';
import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import AppLogo from '../ui/AppLogo';
import { format, startOfDay } from 'date-fns';

const abEnvironment = new AbEnvironment();
const IntegrationSetupRecurlyQuery = graphql`
query IntegrationSetupRecurlyQuery($accountDelegatedAuthsId: UUID!) {
  accountDelegatedAuthsTByAccountDelegatedAuthsId(accountDelegatedAuthsId: $accountDelegatedAuthsId) {
    appName
    authType
    appData
    effectiveDate
    t1
    appIntegrationTByAppIntegrationId {
      appDescription
      appLogo
    }
    accountDelegatedAuthsMapTsByAccountDelegatedAuthsId {
      totalCount
      nodes {
        accountDelegatedAuthsMapId
        mapName
        entityId
        entityName
        entityDescription
      }
    }
    accountTByAccountId {
      accountName
      paymentSourceTsByAccountId(condition: { psStatus: 0 }) {
        totalCount
        nodes {
          psId
          psStatus
          psType
          ccNum
          ccExpy
          ccBrand
          ccLast4
          extSource
          fiName
          achAccountNum
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    },
    vendorSelect: {
      width: 420,
    },
    vendorNameField: {
      width: 360,
    },
    accountSelect: {
      width: 370,
    },
    codeField: {
      width: 100,
    },
    descField: {
      minWidth: 486,
    },
    paper: {
        [theme.breakpoints.up('xs')]: {
          padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(2),
        },
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(3),
        },
        textAlign: 'left',
        color: theme.palette.text.secondary,
        maxWidth: 1200,
        marginTop: 16,
    }
});

class IntegrationSetupRecurly extends Component {

  constructor(props) {
    super(props);

    this.state = {
      effectiveDate: null,
      apiKey: null,
    };
  }

  /*
  componentDidMount() {
    // A load hook may be needed if we need to preload selection information.
  }
  */

  /* Called when the user wants to disconnect.  The called OAuth2Revoke is now misleadingly named, as
     it now also handles apikey disconnect */
  disconnectFromApp(adaId) {
    const { callPartnerApi } = this.props.auth;

    if (! adaId) {
      return;
    }

    let payload = {
          operation: "OAuth2Revoke",
          params: {
            accountDelegatedAuthsId:  adaId
          }
        };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      if (data.link) {
        window.location.replace(data.link);
      } else {
        window.location.reload();
      }
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  /*
   * Placeholder
   */
  isSaveDisabled(accountProps) {


    let retval = this.disableAttributeCheck(this.state.apiKey,  accountProps.accountDelegatedAuthsTByAccountDelegatedAuthsId.t1, false);

    return retval;

  }

  disableAttributeCheck(stateVar, propsVar, allowEmptyString)
  {
      if (stateVar !== null)
      {
          if (propsVar === null && stateVar === '')
              return true;

          if ((propsVar !== null && propsVar !== '') && stateVar === '') {
            if (allowEmptyString) {
              return false;
            } else {
              return true;
            }
          }

          if (propsVar !== stateVar)
              return false;
      }
      return true;
  }

  getValueForRender(state_member, prop_member, theDefault)
  {
    if (state_member != null)
      return state_member;

    if (prop_member != null)
      return prop_member;

    return theDefault;
  }

  setEffectiveDate(aDate) {
    // Set date to the beginning of the day.
    let theDate = startOfDay(aDate);
    this.setState({effectiveDate: theDate});
  }

  getEffectiveDate(propsEffectiveDate) {
    if (this.state.effectiveDate != null) {
      return this.state.effectiveDate;
    } else if (propsEffectiveDate != null) {
      let aDate = null;
      try {
        aDate = new Date(propsEffectiveDate);
      } catch (anErr) {

      }
      if (aDate != null) {
        return aDate;
      }
    }

    return new Date();
  }

  /**
   * Return text as a 'YYYY-MM-DD' string.
   * 
   * This will either be a date on the state, or a date string from props.
   */
  getEffectiveDateString(propsEffectiveDate) {
    let theDate = this.getEffectiveDate(propsEffectiveDate);
    let theEffectiveDateText = format(theDate, 'yyyy-MM-dd');
    return theEffectiveDateText;
  }

  saveSettings(adaId) {
    // Save the api key in the t1 value
    if (this.state.apiKey != null && this.state.apiKey) {
      IntegrationSetupMutation.commit(adaId, null, this.state.apiKey)
      .then(response => {
        window.location.reload();
      })
      .catch(error => {
        // Swallow
        console.log("Error: " + error);
      });
    }

  }

  render() {
    const { isAuthenticatedVendor, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticatedVendor() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }

    const params = new URLSearchParams(this.props.location.search);
    let adaId = params.get('adaId');

    let backText = "Settings";

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={IntegrationSetupRecurlyQuery}
        variables={{accountDelegatedAuthsId: adaId}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let backLink = 'settings';
            if (props.accountDelegatedAuthsTByAccountDelegatedAuthsId === null)
            {              
              return (
                <PageTitle title="Integration not found" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
              );
            }

            // Are there any mappings?
            let theIntegration = props.accountDelegatedAuthsTByAccountDelegatedAuthsId;
            let theLogo = theIntegration.appIntegrationTByAppIntegrationId.appLogo;
            let theDescription = theIntegration.appIntegrationTByAppIntegrationId.appDescription;

            let theEffectiveDateText = this.getEffectiveDateString(theIntegration.effectiveDate);

            return (
              <Grid container spacing={3} direction="row" justifyContent="flex-start">
                <Grid item xs={12}>
                  <PageTitle title="Manage integration" showBackButton={true} name={backText} backLink={backLink} noMargin={true} history={this.props.history}/>
                </Grid>
                <Grid item xs={12}>
                  <Paper className={classes.paper}>
                    <Grid container spacing={2} direction="row" justifyContent="flex-start">
                      <Grid item>
                        <AppLogo logoPath={theLogo} appName={theIntegration.appName} imageHeight='48px'/>
                      </Grid>
                      <Grid item>
                        <Typography variant="h6">{theIntegration.appName}</Typography>
                        <Typography variant="body2" className={classes.description}>{theDescription}</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          Effective date
                        </Typography>
                        <Typography variant="body2" className={classes.description}>
                          When the Recurly integration is established, we'll pull referral transaction data as of the effective date.  
                        </Typography>
                      </Grid>
                      {/* TODO - this should be read-only if the map has already been created {this.getEffectiveDate()}*/}
                      <Grid item>
                        <Typography variant="body1">
                          {theEffectiveDateText}
                        </Typography>

                        { /*
                        <div style={AppBindStyles.datePickerField}>
                          <DatePicker 
                            maxDateMessage={AppBindStyles.datePickerMessageMax}
                            autoOk
                            label="Integration effective date"
                            value={this.getEffectiveDate(theIntegration.effectiveDate)}
                            onChange={(date) => this.setEffectiveDate(date) }
                            format="YYYY-MM-DD"
                            disableFuture
                          />
                        </div> */}
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          id="api-key-name" label="API key"
                          value={this.getValueForRender(this.state.apiKey,    props.accountDelegatedAuthsTByAccountDelegatedAuthsId.t1,    '')}
                          onChange={(e) => this.setState({ apiKey: e.target.value })}
                          inputProps={{ maxLength: 64 }}
                          disabled={false}
                        />
                      </Grid>

                      <Grid item xs={12}/>

                      <Grid item>
                        <Button color="primary" variant="contained" disabled={this.isSaveDisabled(props)} 
                                onClick={()=> {
                                  this.saveSettings(adaId)
                                }}>
                          Save settings
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button color="primary" variant="outlined" onClick={()=> {this.disconnectFromApp(adaId)}}>Disconnect from Recurly</Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            );
          }
          return (
            <LoadingSpinner />
          );
        }}
      />
    );
  }
}

IntegrationSetupRecurly.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(IntegrationSetupRecurly);
